import Speed, {
  type SpeedRef,
  type TestPhase,
  type SpeedServer,
} from './Speed';
import Ping, { type PingRef } from './Ping';
import Tracert, { type TracertRef } from './Tracert';
import Scanner, { type ScannerRef } from './Scanner';
import DiagnosticsHistory from './History';
import {
  ToolsGrid,
  TopControlsRow,
  ToolFilterGroup,
  ToolToggleButton,
  ControlsRow,
  ControlGroup,
  ControlLabel,
  InputWrapper,
  CompactInputWrapper,
  ErrorMessage,
  ActionButton,
  ToolContent,
  HistoryButton,
  ViewPane,
} from './styles';
import { useState, useRef, type MouseEvent, type ChangeEvent } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Activity,
  Gauge,
  History,
  Map,
  Play,
  RotateCcw,
  ScanSearch,
  Server,
} from 'lucide-react';
import Typography from '@mui/material/Typography';
import Card from '@/components/Card';
import DescriptionPopper from '@/components/DescriptionPopper';
import TextField from '@/components/Input/TextField';
import { isValidHost, sanitizeHost } from '@/utils/validators';
import { useTranslation } from 'react-i18next';
import { useEquipmentContext } from '@/context/EquipmentContext';

type ToolOption = 'speed' | 'ping' | 'tracert' | 'scanner';

/** Tools that take a target host, a timeout and optional hop count. */
const HOST_TOOLS: ToolOption[] = ['ping', 'tracert'];

function Tools() {
  const { t } = useTranslation();
  const { equipment } = useEquipmentContext();
  const [currentTool, setCurrentTool] = useState<ToolOption>('speed');
  const [speedServer, setSpeedServer] = useState<SpeedServer>('1');

  // Target IP input for Ping and Tracert
  const [targetIp, setTargetIp] = useState<string>('');
  const [ipError, setIpError] = useState<boolean>(false);
  const [maxHops, setMaxHops] = useState<string>('30');
  const [timeoutVal, setTimeoutVal] = useState<string>('1000');

  const speedRef = useRef<SpeedRef>(null);
  const [speedState, setSpeedState] = useState<{
    phase: TestPhase;
    isRunning: boolean;
  }>({
    phase: 'idle',
    isRunning: false,
  });

  const pingRef = useRef<PingRef>(null);
  const [pingState, setPingState] = useState<{
    isRunning: boolean;
    isDone: boolean;
  }>({
    isRunning: false,
    isDone: false,
  });

  const tracertRef = useRef<TracertRef>(null);
  const [tracertState, setTracertState] = useState<{
    isRunning: boolean;
    isDone: boolean;
  }>({
    isRunning: false,
    isDone: false,
  });

  const scannerRef = useRef<ScannerRef>(null);
  const [scannerState, setScannerState] = useState<{
    isRunning: boolean;
    isDone: boolean;
  }>({
    isRunning: false,
    isDone: false,
  });

  const isHostTool = HOST_TOOLS.includes(currentTool);

  const [showHistory, setShowHistory] = useState(false);

  const handleIpChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setTargetIp(value);
    if (value && !isValidHost(value)) {
      setIpError(true);
    } else {
      setIpError(false);
    }
  };

  const handleActionClick = () => {
    if (currentTool === 'speed') {
      if (speedState.phase === 'idle') {
        speedRef.current?.startTest();
      } else {
        speedRef.current?.resetTest();
      }
    } else if (currentTool === 'ping') {
      if (!pingState.isRunning && !pingState.isDone) {
        const cleanHost = sanitizeHost(targetIp);
        setTargetIp(cleanHost);
        pingRef.current?.startTest(cleanHost, timeoutVal || '1000');
      } else {
        pingRef.current?.resetTest();
      }
    } else if (currentTool === 'tracert') {
      if (!tracertState.isRunning && !tracertState.isDone) {
        const cleanHost = sanitizeHost(targetIp);
        setTargetIp(cleanHost);
        tracertRef.current?.startTrace(
          cleanHost,
          maxHops || '30',
          timeoutVal || '5000'
        );
      } else {
        tracertRef.current?.resetTrace();
      }
    } else if (currentTool === 'scanner') {
      scannerRef.current?.startTest();
    }
  };

  const isIdle = () => {
    if (currentTool === 'speed') return speedState.phase === 'idle';
    if (currentTool === 'ping')
      return !pingState.isRunning && !pingState.isDone;
    if (currentTool === 'tracert')
      return !tracertState.isRunning && !tracertState.isDone;
    if (currentTool === 'scanner')
      return !scannerState.isRunning && !scannerState.isDone;
    return true;
  };

  const isRunning = () => {
    if (currentTool === 'speed') return speedState.isRunning;
    if (currentTool === 'ping') return pingState.isRunning;
    if (currentTool === 'tracert') return tracertState.isRunning;
    if (currentTool === 'scanner') return scannerState.isRunning;
    return false;
  };

  const buttonIcon = isRunning() ? (
    <CircularProgress size="16px" color="inherit" />
  ) : currentTool === 'scanner' ? (
    <ScanSearch size="16px" />
  ) : !isIdle() ? (
    <RotateCcw size="16px" />
  ) : (
    <Play size="16px" />
  );

  const buttonLabel =
    currentTool === 'scanner'
      ? isRunning()
        ? t('scanner.scanningButton')
        : t('scanner.scanButton')
      : isRunning()
        ? t('equipment.tools.sending')
        : !isIdle()
          ? t('equipment.tools.testAgain')
          : t('equipment.tools.startTest');

  const buttonVariant: 'primary' | 'outlined' = isIdle()
    ? 'primary'
    : 'outlined';

  const disableButton = (isHostTool && (!targetIp || ipError)) || isRunning();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !disableButton) {
      handleActionClick();
    }
  };

  return (
    <ToolsGrid>
      <ViewPane $active={!showHistory}>
        <TopControlsRow>
          {/** Tool type */}
          <ControlGroup>
            <ToolFilterGroup
              value={currentTool}
              exclusive
              onChange={(
                _event: MouseEvent<HTMLElement>,
                value: ToolOption | null
              ) => {
                if (value !== null) {
                  setCurrentTool(value);
                  if (value === 'tracert' && timeoutVal === '1000') {
                    setTimeoutVal('5000');
                  } else if (value === 'ping' && timeoutVal === '5000') {
                    setTimeoutVal('1000');
                  }
                }
              }}
            >
              <ToolToggleButton value="speed">
                <Gauge size="16px" />
                {t('tools.speed.title')}
              </ToolToggleButton>
              <ToolToggleButton value="ping">
                <Activity size="16px" />
                {t('tools.ping.title')}
              </ToolToggleButton>
              <ToolToggleButton value="tracert">
                <Map size="16px" />
                {t('tools.traceroute.title')}
              </ToolToggleButton>
              <ToolToggleButton value="scanner">
                <ScanSearch size="16px" />
                {t('scanner.title')}
              </ToolToggleButton>
            </ToolFilterGroup>
          </ControlGroup>

          {/** Server selector (option 1 or 2) */}
          {currentTool === 'speed' && (
            <ControlGroup>
              <ControlLabel>{t('tools.speed.server')}</ControlLabel>
              <ToolFilterGroup
                value={speedServer}
                exclusive
                onChange={(
                  _event: MouseEvent<HTMLElement>,
                  value: SpeedServer | null
                ) => {
                  if (value !== null && !isRunning()) {
                    setSpeedServer(value);
                    speedRef.current?.resetTest();
                  }
                }}
              >
                <ToolToggleButton value="1" disabled={isRunning()}>
                  <Server size="14px" />
                  {t('tools.speed.server1')}
                </ToolToggleButton>
                <ToolToggleButton value="2" disabled={isRunning()}>
                  <Server size="14px" />
                  {t('tools.speed.server2')}
                </ToolToggleButton>
              </ToolFilterGroup>
            </ControlGroup>
          )}

          {/** Input controls row */}
          <ControlsRow>
            {isHostTool && (
              <ControlGroup>
                <ControlLabel>
                  {t('equipment.latency.description')}
                </ControlLabel>
                {/** IP Address to ping and tracert test */}
                <InputWrapper>
                  <TextField
                    value={targetIp}
                    onChange={handleIpChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t('equipment.latency.placeholder')}
                    width="100%"
                    disabled={isRunning()}
                    error={ipError}
                  />
                  {ipError && (
                    <ErrorMessage>
                      {t('equipment.latency.invalidHost')}
                    </ErrorMessage>
                  )}
                </InputWrapper>
              </ControlGroup>
            )}

            {isHostTool && (
              <ControlGroup>
                <ControlLabel>{t('equipment.tools.timeout')}</ControlLabel>
                <CompactInputWrapper>
                  <TextField
                    type="number"
                    value={timeoutVal}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => setTimeoutVal(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={currentTool === 'tracert' ? '5000' : '1000'}
                    width="100%"
                    disabled={isRunning()}
                  />
                </CompactInputWrapper>
              </ControlGroup>
            )}

            {currentTool === 'tracert' && (
              <ControlGroup>
                <ControlLabel>{t('tools.traceroute.maxHopCount')}</ControlLabel>
                <CompactInputWrapper>
                  <TextField
                    type="number"
                    value={maxHops}
                    onChange={(
                      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => setMaxHops(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="30"
                    width="100%"
                    disabled={isRunning()}
                  />
                </CompactInputWrapper>
              </ControlGroup>
            )}

            <ActionButton
              variant={buttonVariant}
              startIcon={buttonIcon}
              onClick={handleActionClick}
              disabled={disableButton}
            >
              {buttonLabel}
            </ActionButton>
          </ControlsRow>

          {/** Diagnostics history */}
          <DescriptionPopper
            sx={{ marginLeft: 'auto' }}
            placement="top"
            description={
              <Card padding="8px 12px">
                <Typography variant="caption">
                  {t('equipment.tools.history.title')}
                </Typography>
              </Card>
            }
          >
            <HistoryButton
              aria-label={t('equipment.tools.history.title')}
              onClick={() => setShowHistory(true)}
            >
              <History size="18px" />
            </HistoryButton>
          </DescriptionPopper>
        </TopControlsRow>

        {/** Speed */}
        <ToolContent $active={currentTool === 'speed'}>
          <Speed
            ref={speedRef}
            onStateChange={setSpeedState}
            equipmentId={String(equipment?.id || '')}
            server={speedServer}
          />
        </ToolContent>

        {/** Ping */}
        <ToolContent $active={currentTool === 'ping'}>
          <Ping
            ref={pingRef}
            onStateChange={setPingState}
            equipmentId={String(equipment?.id || '')}
          />
        </ToolContent>

        {/** Tracert */}
        <ToolContent $active={currentTool === 'tracert'}>
          <Tracert
            ref={tracertRef}
            onStateChange={setTracertState}
            equipmentId={String(equipment?.id || '')}
          />
        </ToolContent>

        {/** Wi-Fi Scanner */}
        <ToolContent $active={currentTool === 'scanner'}>
          <Scanner ref={scannerRef} onStateChange={setScannerState} />
        </ToolContent>
      </ViewPane>

      {/** History — kept mounted alongside the tools so a diagnostic running
       *  in the background is not aborted by switching views. */}
      <ViewPane $active={showHistory}>
        {showHistory && (
          <DiagnosticsHistory onBack={() => setShowHistory(false)} />
        )}
      </ViewPane>
    </ToolsGrid>
  );
}

export default Tools;
